import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { CheckoutUpsellPopup } from '../components/CheckoutUpsellPopup';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { cn } from '../lib/utils';
import { getCrewRefCode, clearCrewRefCode } from '../hooks/useCrewTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, sessionId, refreshCart } = useCart();
  const { formatPrice, currency } = useCurrency();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [cgvError, setCgvError] = useState(false);
  const [showUpsellPopup, setShowUpsellPopup] = useState(false);
  const [upsellShown, setUpsellShown] = useState(false);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    country: 'IL',
    orderNote: ''
  });

  // Vérifier si on revient de Stripe avec cancelled=true
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      setPaymentCancelled(true);
      // Récupérer les infos sauvegardées si disponibles
      const pendingOrder = localStorage.getItem('mutagim_pending_order');
      if (pendingOrder) {
        try {
          const order = JSON.parse(pendingOrder);
          setError(`Paiement annulé pour la commande ${order.orderNumber}. Vous pouvez réessayer.`);
        } catch (e) {
          setError('Paiement annulé. Vous pouvez réessayer.');
        }
        localStorage.removeItem('mutagim_pending_order');
      }
    }
  }, [searchParams]);

  // Show upsell popup on first load if cart < 100€
  useEffect(() => {
    if (!upsellShown && cart.subtotal < 100 && cart.items.length > 0) {
      const timer = setTimeout(() => {
        setShowUpsellPopup(true);
        setUpsellShown(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [cart.subtotal, cart.items.length, upsellShown]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCgvChange = (checked) => {
    setCgvAccepted(checked);
    if (checked) setCgvError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier CGV
    if (!cgvAccepted) {
      setCgvError(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Récupérer le code Crew depuis le cookie (si existe)
      const crewRefCode = getCrewRefCode();
      
      // ÉTAPE 1: Créer la commande
      const orderResponse = await axios.post(`${API_URL}/api/checkout/create`, {
        shipping_address: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          address_line_1: formData.address,
          address_line_2: formData.addressLine2 || null,
          city: formData.city,
          postal_code: formData.postalCode,
          country_code: formData.country,
          phone: formData.phone || null
        },
        billing_same_as_shipping: true,
        customer_note: formData.orderNote || null,
        cgv_accepted: true,
        customer_email: formData.email || null,
        crew_ref_code: crewRefCode
      }, {
        headers: { 'X-Cart-Session': sessionId }
      });

      // Effacer le cookie Crew après utilisation
      if (crewRefCode) {
        clearCrewRefCode();
      }

      const orderId = orderResponse.data.order_id;
      const orderNumber = orderResponse.data.order_number;
      
      // Sauvegarder le numéro de commande pour la page de retour
      localStorage.setItem('mutagim_pending_order', JSON.stringify({
        orderId,
        orderNumber,
        total: orderResponse.data.total,
        currency: orderResponse.data.currency
      }));

      // ÉTAPE 2: Créer la session de paiement Stripe
      const stripeResponse = await axios.post(`${API_URL}/api/payments/stripe/checkout`, {
        order_id: orderId,
        provider: 'stripe',
        origin_url: window.location.origin
      }, {
        headers: { 'X-Cart-Session': sessionId }
      });

      // ÉTAPE 3: Rediriger vers Stripe Checkout
      if (stripeResponse.data.url) {
        // Rafraîchir le panier avant la redirection
        refreshCart();
        // Redirection vers Stripe
        window.location.href = stripeResponse.data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || err.message || 'Erreur lors du paiement');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-mutagim-dark">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">
              Commande confirmée !
            </h1>
            <p className="text-gray-400 mb-4">
              Merci pour votre commande. Vous recevrez un email de confirmation.
            </p>
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800 mb-6">
              <p className="text-gray-400">Numéro de commande</p>
              <p className="text-xl font-bold text-mutagim-red">{success.orderNumber}</p>
              <p className="text-white mt-2">Total: {success.total} {success.currency}</p>
            </div>
            <Button 
              onClick={() => navigate('/')} 
              className="bg-mutagim-red hover:bg-red-700"
            >
              Retour à l'accueil
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (cart.items.length === 0 && !success) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark" data-testid="checkout-page">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-8">
          {t('checkout.title')}
        </h1>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Shipping Address Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
              <h2 className="text-xl font-bold text-white mb-6">
                {t('checkout.shippingAddress')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-gray-300">
                    {t('checkout.firstName')} *
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                    data-testid="input-firstName"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-gray-300">
                    {t('checkout.lastName')} *
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                    data-testid="input-lastName"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300">
                    {t('checkout.email')} *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-gray-300">
                    {t('checkout.phone')}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                    data-testid="input-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address" className="text-gray-300">
                    {t('checkout.address')} *
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                    data-testid="input-address"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="addressLine2" className="text-gray-300">
                    {t('checkout.addressLine2')}
                  </Label>
                  <Input
                    id="addressLine2"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleInputChange}
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                    data-testid="input-addressLine2"
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="text-gray-300">
                    {t('checkout.city')} *
                  </Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label htmlFor="postalCode" className="text-gray-300">
                    {t('checkout.postalCode')} *
                  </Label>
                  <Input
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-gray-800 border-gray-700 text-white"
                    data-testid="input-postalCode"
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="orderNote" className="text-gray-300">
                  {t('checkout.orderNote')}
                </Label>
                <Input
                  id="orderNote"
                  name="orderNote"
                  value={formData.orderNote}
                  onChange={handleInputChange}
                  className="mt-1 bg-gray-800 border-gray-700 text-white"
                  data-testid="input-orderNote"
                />
              </div>
            </div>

            {/* CGV Checkbox - OBLIGATOIRE */}
            <div className={cn(
              "bg-gray-900 rounded-lg p-6 border",
              cgvError ? "border-red-500" : "border-gray-800"
            )}>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="cgv"
                  checked={cgvAccepted}
                  onCheckedChange={handleCgvChange}
                  className={cn(
                    "mt-1 border-gray-600 data-[state=checked]:bg-mutagim-red data-[state=checked]:border-mutagim-red",
                    cgvError && "border-red-500"
                  )}
                  data-testid="cgv-checkbox"
                />
                <div>
                  <Label 
                    htmlFor="cgv" 
                    className={cn(
                      "text-gray-300 cursor-pointer",
                      cgvError && "text-red-500"
                    )}
                  >
                    {t('checkout.cgvAccept')} *
                  </Label>
                  <a 
                    href="/terms" 
                    target="_blank" 
                    className="block text-sm text-mutagim-red hover:underline mt-1"
                  >
                    {t('footer.terms')}
                  </a>
                </div>
              </div>
              {cgvError && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-1" data-testid="cgv-error">
                  <AlertCircle className="h-4 w-4" />
                  {t('checkout.cgvRequired')}
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-900/30 border border-red-500 rounded-lg p-4 text-red-300" data-testid="checkout-error">
                {error}
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 rounded-lg p-6 border border-gray-800 sticky top-24">
              <h2 className="text-xl font-bold text-white mb-4">
                {t('checkout.orderSummary')}
              </h2>

              {/* Cart items summary */}
              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {cart.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-800 rounded overflow-hidden flex-shrink-0">
                      <img
                        src={item.product?.image || '/placeholder-product.jpg'}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">
                        {item.product?.name?.[i18n.language] || item.product?.name?.fr}
                      </p>
                      <p className="text-gray-500 text-xs">x{item.quantity}</p>
                    </div>
                    <p className="text-gray-300 text-sm">
                      {formatPrice(item.line_total)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="space-y-3 border-t border-gray-800 pt-4">
                <div className="flex justify-between text-gray-400">
                  <span>{t('cart.subtotal')}</span>
                  <span>{formatPrice(cart.subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>{t('cart.shipping')}</span>
                  <span className="text-green-500">Gratuit</span>
                </div>
                <div className="flex justify-between text-white font-bold text-lg border-t border-gray-800 pt-3">
                  <span>{t('cart.total')}</span>
                  <span className="text-mutagim-red">{formatPrice(cart.subtotal)}</span>
                </div>
              </div>

              <Button 
                type="submit"
                className="w-full mt-6 bg-mutagim-red hover:bg-red-700 text-white font-semibold py-6"
                disabled={loading}
                data-testid="place-order-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin me-2" />
                    Redirection vers le paiement...
                  </>
                ) : (
                  <>
                    🔒 {t('checkout.payNow', 'Payer')} {formatPrice(cart.subtotal)}
                  </>
                )}
              </Button>
              
              <p className="text-xs text-gray-500 text-center mt-3">
                Paiement sécurisé par Stripe
              </p>
            </div>
          </div>
        </form>
      </main>
      
      <Footer />
      
      {/* Upsell Popup (Phase 15) */}
      {showUpsellPopup && (
        <CheckoutUpsellPopup 
          cartTotal={cart.subtotal}
          onClose={() => setShowUpsellPopup(false)}
        />
      )}
    </div>
  );
}

export default CheckoutPage;
